import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, ChevronDown, Mail, MapPin } from 'lucide-react';

const SECTIONS = [
  {
    id: 'information',
    title: '1. Information We Collect',
    content: `We collect information you provide directly to us when you use our services, fill out forms, or contact us. This includes:

**Personal Identification Information:** Name, email address, phone number, company name, and job title when you submit enquiry forms or request services.

**Technical Information:** IP address, browser type, operating system, referring URLs, pages visited, and time spent on pages — collected automatically via cookies and analytics tools (Google Analytics).

**Communication Data:** Messages, project requirements, and any information you share when communicating with us via email, WhatsApp, or our contact forms.

**Payment Information:** We use milestone-based payments. Payment details are processed via secure third-party payment gateways. We do not store full payment card details.`,
  },
  {
    id: 'use',
    title: '2. How We Use Your Information',
    content: `We use the information we collect for the following purposes:

• **Service Delivery:** To provide, operate, and improve our software development, digital marketing, and consulting services.
• **Communication:** To respond to enquiries, send project updates, proposals, and service-related notifications.
• **Billing:** To process payments, send invoices, and manage contracts for services rendered.
• **Marketing:** With your consent, to send newsletters, service announcements, and promotional offers. You may opt out at any time.
• **Analytics:** To understand how visitors interact with our website and improve user experience.
• **Legal Compliance:** To comply with applicable laws, regulations, and legal processes.`,
  },
  {
    id: 'sharing',
    title: '3. Information Sharing & Disclosure',
    content: `We do not sell, trade, or rent your personal information to third parties. We may share your information only in these circumstances:

• **Service Providers:** Trusted third-party vendors (e.g., Formspree for forms, Google Analytics for website analytics, hosting providers) who assist us in operating our services, subject to confidentiality agreements.
• **Business Partners:** With your explicit consent, to collaborate with development partners on your specific project.
• **Legal Requirements:** When required by law, court order, or governmental authority.
• **Business Transfers:** In the event of a merger, acquisition, or sale of assets, your information may be transferred. You will be notified prior to such transfer.
• **Protection of Rights:** To protect the rights, property, or safety of Vibhraad Technology, our clients, or the public.`,
  },
  {
    id: 'cookies',
    title: '4. Cookies & Tracking Technologies',
    content: `We use cookies and similar tracking technologies to enhance your browsing experience:

• **Essential Cookies:** Required for the website to function properly (session management, security).
• **Analytics Cookies:** Google Analytics cookies that help us understand website traffic and usage patterns.
• **Preference Cookies:** Remember your settings and preferences for future visits.

You can control cookie settings through your browser. Disabling certain cookies may affect website functionality. See our Cookie Policy for detailed information.`,
  },
  {
    id: 'security',
    title: '5. Data Security',
    content: `We implement industry-standard security measures to protect your personal information:

• SSL/TLS encryption for all data transmitted to and from our website.
• Secure, access-controlled servers for data storage.
• Regular security audits and vulnerability assessments.
• Restricted employee access to personal data on a need-to-know basis.
• Data retention policies ensuring information is deleted when no longer needed.

While we take reasonable precautions, no method of transmission over the internet is 100% secure. We encourage you to use strong passwords and protect your account credentials.`,
  },
  {
    id: 'rights',
    title: '6. Your Rights & Choices',
    content: `You have the following rights regarding your personal information:

• **Access:** Request a copy of the personal information we hold about you.
• **Correction:** Request correction of inaccurate or incomplete information.
• **Deletion:** Request deletion of your personal information, subject to legal retention requirements.
• **Opt-Out:** Unsubscribe from marketing communications at any time via the unsubscribe link in emails or by contacting us.
• **Data Portability:** Request your data in a portable, machine-readable format.
• **Withdraw Consent:** Where processing is based on consent, withdraw it at any time.

To exercise any of these rights, contact us at vibhraad@gmail.com with your request.`,
  },
  {
    id: 'retention',
    title: '7. Data Retention',
    content: `We retain personal information for as long as necessary to fulfill the purposes described in this policy:

• **Client Data:** Retained for the duration of the business relationship plus 7 years for legal and accounting compliance.
• **Enquiry Data:** Contact form submissions retained for 2 years if no project is initiated.
• **Analytics Data:** Aggregated analytics data retained for 26 months (Google Analytics default).
• **Marketing Data:** Email subscriber data retained until you unsubscribe or request deletion.

After retention periods, data is securely deleted or anonymised.`,
  },
  {
    id: 'international',
    title: '8. International Data Transfers',
    content: `Vibhraad Technology is headquartered in India. If you access our services from outside India, your information may be transferred to and processed in India.

We serve clients in 25+ countries. When transferring data internationally, we ensure appropriate safeguards are in place, including data processing agreements and adherence to applicable data protection laws.

By using our services, you consent to the transfer of your information to India and its processing in accordance with this Privacy Policy.`,
  },
  {
    id: 'children',
    title: '9. Children\'s Privacy',
    content: `Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from minors.

If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately at vibhraad@gmail.com. We will promptly delete such information from our systems.`,
  },
  {
    id: 'updates',
    title: '10. Updates to This Policy',
    content: `We may update this Privacy Policy periodically to reflect changes in our practices, technology, or legal requirements.

• Updates will be posted on this page with a revised "Last Updated" date.
• For significant changes, we will notify active clients via email.
• Continued use of our services after changes constitutes acceptance of the updated policy.

We encourage you to review this policy periodically. If you disagree with any changes, please discontinue use of our services and contact us to delete your data.`,
  },
];

function AccordionItem({ section, index }) {
  const [open, setOpen] = useState(index === 0);

  const formatContent = (text) => {
    return text.split('\n').map((line, i) => {
      if (line.trim() === '') return <br key={i} />;
      const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return <p key={i} className="mb-2 text-gray-600 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: formatted }} />;
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.04 }}
      className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm mb-3"
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="font-bold text-gray-900 text-sm">{section.title}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }}>
          <ChevronDown className="w-5 h-5 text-blue-500 flex-shrink-0" />
        </motion.span>
      </button>
      {open && (
        <div className="px-6 pb-5 pt-2 bg-gray-50/50 border-t border-gray-100">
          {formatContent(section.content)}
        </div>
      )}
    </motion.div>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="bg-white min-h-screen">
      {/* Hero */}
      <section className="bg-gray-950 py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-blue-700/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-5 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-600/30">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">Privacy Policy</h1>
            <p className="text-gray-400 text-base max-w-xl mx-auto leading-relaxed mb-4">
              Vibhraad Technology is committed to protecting your privacy and handling your data with transparency and integrity.
            </p>
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-gray-400 text-xs px-4 py-2 rounded-full">
              Last Updated: April 16, 2026 · Effective: October 2025
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 max-w-4xl mx-auto px-5">
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 mb-8">
          <p className="text-blue-800 text-sm leading-relaxed">
            <strong>Summary:</strong> We collect only what we need, use it to deliver and improve our services, never sell it, and protect it with industry-standard security. You can request access, correction, or deletion of your data at any time.
          </p>
        </div>

        {SECTIONS.map((section, i) => (
          <AccordionItem key={section.id} section={section} index={i} />
        ))}

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-10 bg-gray-950 rounded-3xl p-8 text-white"
        >
          <h2 className="text-xl font-bold mb-2">Questions About This Policy?</h2>
          <p className="text-gray-400 text-sm mb-5">Contact our data protection team — we respond within 48 hours.</p>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600/20 rounded-lg flex items-center justify-center">
                <Mail className="w-4 h-4 text-blue-400" />
              </div>
              <a href="mailto:vibhraad@gmail.com" className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors">vibhraad@gmail.com</a>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600/20 rounded-lg flex items-center justify-center">
                <MapPin className="w-4 h-4 text-blue-400" />
              </div>
              <span className="text-gray-400 text-sm">Vibhraad Technology Pvt Ltd, F-375, Sector-36, Noida, UP 201301</span>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
